import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SaveIcon, XIcon } from 'lucide-react';
import { BudgetType } from './BudgetManagement';
interface BudgetTypesProps {
  budgetTypes: BudgetType[];
  onCreateBudgetType: (budgetType: Omit<BudgetType, 'id'>) => void;
  onUpdateBudgetType: (budgetType: BudgetType) => void;
  onDeleteBudgetType: (budgetTypeId: string) => void;
  locale: string;
}
export function BudgetTypes({
  budgetTypes,
  onCreateBudgetType,
  onUpdateBudgetType,
  onDeleteBudgetType,
  locale
}: BudgetTypesProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<BudgetType, 'id'>>({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setErrors({});
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Budget type name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleAddClick = () => {
    setIsAdding(true);
    resetForm();
  };
  const handleEditClick = (budgetType: BudgetType) => {
    setEditingTypeId(budgetType.id);
    setFormData({
      name: budgetType.name,
      description: budgetType.description
    });
    setErrors({});
  };
  const handleCancelClick = () => {
    setIsAdding(false);
    setEditingTypeId(null);
    resetForm();
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (editingTypeId) {
        onUpdateBudgetType({
          id: editingTypeId,
          ...formData
        });
        setEditingTypeId(null);
      } else {
        onCreateBudgetType(formData);
        setIsAdding(false);
      }
      resetForm();
    }
  };
  return <div className="space-y-6" data-id="element-8838">
      <div className="flex justify-between items-center" data-id="element-8839">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-8840">
          Budget Types
        </h3>
        {!isAdding && !editingTypeId && <button type="button" onClick={handleAddClick} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8841">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-8842" />
            Add Budget Type
          </button>}
      </div>
      {(isAdding || editingTypeId) && <form onSubmit={handleSubmit} className="bg-white shadow sm:rounded-md sm:overflow-hidden" data-id="element-8843">
          <div className="px-4 py-5 sm:p-6" data-id="element-8844">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8845">
              <div className="sm:col-span-3" data-id="element-8846">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-8847">
                  Budget Type Name *
                </label>
                <div className="mt-1" data-id="element-8848">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} data-id="element-8849" />
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-8850">{errors.name}</p>}
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-8851">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-8852">
                  Description
                </label>
                <div className="mt-1" data-id="element-8853">
                  <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8854" />
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-8855">
            <button type="button" onClick={handleCancelClick} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8856">
              <XIcon className="h-4 w-4 mr-2" data-id="element-8857" />
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8858">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-8859" />
              {editingTypeId ? 'Update Budget Type' : 'Add Budget Type'}
            </button>
          </div>
        </form>}
      <div className="flex flex-col" data-id="element-8860">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-8861">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-8862">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-8863">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-8864">
                <thead className="bg-gray-50" data-id="element-8865">
                  <tr data-id="element-8866">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8867">
                      Budget Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8868">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8869">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-8870">
                  {budgetTypes.length === 0 ? <tr data-id="element-8871">
                      <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-8872">
                        No budget types found
                      </td>
                    </tr> : budgetTypes.map(type => <tr key={type.id} data-id="element-8873">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-8874">
                          {type.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-8875">
                          {type.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8876">
                          <div className="flex justify-end space-x-2" data-id="element-8877">
                            <button onClick={() => handleEditClick(type)} className="text-indigo-600 hover:text-indigo-900" title="Edit" disabled={isAdding || editingTypeId !== null} data-id="element-8878">
                              <EditIcon className="h-4 w-4" data-id="element-8879" />
                            </button>
                            <button onClick={() => onDeleteBudgetType(type.id)} className="text-red-600 hover:text-red-900" title="Delete" disabled={isAdding || editingTypeId !== null} data-id="element-8880">
                              <TrashIcon className="h-4 w-4" data-id="element-8881" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}